import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Button,
  TextField,
  Stack,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox
} from '@mui/material';
import { MIconButton } from 'components/@material-extend';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import { axios } from 'utils/axios';
import roundManageSearch from '@iconify/icons-ic/round-manage-search';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import { DatePicker } from '@mui/lab';
import { CheckBox } from '@mui/icons-material';

interface CompanyFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: any;
  setFilters: React.Dispatch<React.SetStateAction<any>>;
  onApply: () => void;
  onClear: () => void;
}

const CompanyFilterDrawer: React.FC<CompanyFilterDrawerProps> = ({
  open,
  onClose,
  filters,
  setFilters,
  onApply,
  onClear
}) => {
  const handleMultiCheckboxChange =
    (field: string, value: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;

      setFilters((prevFilters: any) => {
        const currentValues = prevFilters[field]?.split(',').filter((v: any) => v.trim()) || [];
        const updatedValues = isChecked
          ? Array.from(new Set([...currentValues, value])) // Add the value if checked
          : currentValues.filter((v: any) => v !== value); // Remove the value if unchecked

        return {
          ...prevFilters,
          [field]: updatedValues.join(',')
        };
      });
    };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 2 }}
      >
        <Typography variant="subtitle1" sx={{ ml: 1 }}>
          Filters
        </Typography>
        <MIconButton onClick={onClose}>
          <Icon icon={closeFill} width={20} height={20} />
        </MIconButton>
      </Stack>

      <Divider />
      <Stack spacing={3} sx={{ p: 3 }}>
        {/* Page Size */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Page Size
          </Typography>
          <FormControl fullWidth>
            <Select
              value={filters.pageSize}
              onChange={(e) => setFilters({ ...filters, pageSize: e.target.value })}
            >
              <MenuItem value="10">10</MenuItem>
              <MenuItem value="25">25</MenuItem>
              <MenuItem value="50">50</MenuItem>
            </Select>
          </FormControl>
        </div>
        {/* Search */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Search
          </Typography>
          <TextField
            label="Search"
            fullWidth
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>

        {/* Post Code */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            company Name
          </Typography>
          <TextField
            label="company Name"
            fullWidth
            value={filters.companyName}
            onChange={(e) => setFilters({ ...filters, companyName: e.target.value })}
          />
        </div>
        {/* Address */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            company Reference
          </Typography>
          <TextField
            label="company Reference"
            fullWidth
            value={filters.address}
            onChange={(e) => setFilters({ ...filters, companyReference: e.target.value })}
          />
        </div>
        {/* Contact Name */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Vat Registration Number
          </Typography>
          <TextField
            label="Vat Registration Number"
            fullWidth
            value={filters.vatRegistrationNumber}
            onChange={(e) => setFilters({ ...filters, vatRegistrationNumber: e.target.value })}
          />
        </div>
        {/* Name */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Company Registration Number
          </Typography>
          <TextField
            label="Company Registration Number"
            fullWidth
            value={filters.companyRegistrationNumber}
            onChange={(e) => setFilters({ ...filters, companyRegistrationNumber: e.target.value })}
          />
        </div>
        {/* Owner (Checkboxes) */}
        <div>
          <Typography variant="subtitle1" gutterBottom>
            Default Company
          </Typography>
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={(filters.defaultCompany || '').includes('1')}
                  onChange={handleMultiCheckboxChange('defaultCompany', '1')}
                />
              }
              label="Yes"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={(filters.defaultCompany || '').includes('0')}
                  onChange={handleMultiCheckboxChange('defaultCompany', '0')}
                />
              }
              label="No"
            />
          </FormControl>
        </div>

        <div>
          <Typography variant="subtitle1" gutterBottom>
            Disposal Site
          </Typography>
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={(filters.disposalSite || '').includes('1')}
                  onChange={handleMultiCheckboxChange('disposalSite', '1')}
                />
              }
              label="Yes"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={(filters.disposalSite || '').includes('0')}
                  onChange={handleMultiCheckboxChange('disposalSite', '0')}
                />
              }
              label="No"
            />
          </FormControl>
        </div>
      </Stack>
      {/* Buttons */}
      <Box sx={{ p: 3, pb: 0 }}>
        <Button
          fullWidth
          size="large"
          type="submit"
          color="primary"
          variant="outlined"
          onClick={onApply}
          startIcon={<Icon icon={roundManageSearch} />}
        >
          Apply Filters
        </Button>
      </Box>
      <Box sx={{ p: 3 }}>
        <Button
          fullWidth
          size="large"
          type="submit"
          color="secondary"
          variant="outlined"
          onClick={onClear}
          startIcon={<Icon icon={roundFilterList} />}
        >
          Clear
        </Button>
      </Box>
    </Drawer>
  );
};

export default CompanyFilterDrawer;
